import { useEffect, useState, useRef, useCallback } from "react";
import { connectSocket, AUTH_STORAGE_KEY } from "../../socket";

export default function GlobalCallListener({ setVideoCall }) {
  const ringtoneRef     = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const socketRef       = useRef(null);
  const incomingCallRef = useRef(null);
  const isAcceptedRef   = useRef(false); // ✅ prevent reject after accept

  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  const stopRingtone = useCallback(() => {
    try {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      }
    } catch {}
  }, []);

  useEffect(() => {
    let currentUser = null;
    try {
      const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      currentUser  = parsed?.user?.email;
    } catch {}

    if (!currentUser) return;

    socketRef.current = connectSocket();
    const socket = socketRef.current;
    socket.emit("join", currentUser);

    const handleIncoming = ({ from, type }) => {
      // ✅ Only handle 1-to-1 video calls
      if (type !== "video") return;
      // ✅ Ignore if it came from ourselves
      if (from === currentUser) return;

      setIncomingCall({ from, offer: null });
      isAcceptedRef.current = false;

      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio("/ring.mp3");
        ringtoneRef.current.loop = true;
      }
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch(() => {});
    };

    const handleOffer = ({ from, offer, type }) => {
      // ✅ Only handle 1-to-1 video — ignore group offers
      if (type !== "video") return;
      if (from === currentUser) return;

      setIncomingCall(prev => {
        if (!prev || prev.from !== from) return prev;
        return { ...prev, offer };
      });
    };

    const handleEnd = ({ from }) => {
      const current = incomingCallRef.current;
      if (!current) return;
      if (current.from !== from) return;
      // ✅ Don't dismiss if already accepted
      if (isAcceptedRef.current) return;

      stopRingtone();
      setIncomingCall(null);
      setVideoCall(null);
    };

    socket.off("incoming_call", handleIncoming);
    socket.off("call_offer",    handleOffer);
    socket.off("call_end",      handleEnd);

    socket.on("incoming_call", handleIncoming);
    socket.on("call_offer",    handleOffer);
    socket.on("call_end",      handleEnd);

    return () => {
      socket.off("incoming_call", handleIncoming);
      socket.off("call_offer",    handleOffer);
      socket.off("call_end",      handleEnd);
      stopRingtone();
    };
  }, []);

  const acceptCall = useCallback(() => {
    const call = incomingCallRef.current;
    if (!call?.offer) {
      alert("Wait... connecting");
      return;
    }
    isAcceptedRef.current = true;
    stopRingtone();
    setIncomingCall(null);

    setTimeout(() => {
      setVideoCall({
        remoteUser:    call.from,
        incomingOffer: call.offer,
        isCaller:      false,
      });
    }, 50);
  }, [stopRingtone, setVideoCall]);

  const rejectCall = useCallback(() => {
    if (isAcceptedRef.current) return;
    const call = incomingCallRef.current;
    if (!call) return;
    stopRingtone();
    socketRef.current?.emit("call_end", { to: call.from });
    setIncomingCall(null);
  }, [stopRingtone]);

  if (!incomingCall) return null;

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.6)",
      display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 9999
    }}>
      <div style={{
        background: "#fff", padding: 30,
        borderRadius: 10, textAlign: "center", minWidth: 320
      }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🎥</div>
        <h2>Incoming Video Call</h2>
        <p><b>{incomingCall.from}</b> is calling...</p>
        <div style={{ marginTop: 20, display: "flex", gap: 12, justifyContent: "center" }}>
          <button onClick={acceptCall} style={{
            padding: "10px 20px", background: "#4CAF50",
            color: "#fff", border: "none", borderRadius: 5, cursor: "pointer"
          }}>
            {incomingCall.offer ? "✅ Accept" : "Connecting..."}
          </button>
          <button onClick={rejectCall} style={{
            padding: "10px 20px", background: "#f44336",
            color: "#fff", border: "none", borderRadius: 5, cursor: "pointer"
          }}>
            ❌ Reject
          </button>
        </div>
      </div>
    </div>
  );
}